import { V2Button, V2Typography } from "@cakewalk/ui/v2";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * SMS "resend code" control with a live cooldown countdown.
 *
 * The countdown is deliberately ISOLATED in its own component: its per-second
 * tick re-renders only this subtree, never the sibling OTP `<input>`. That keeps
 * the code field's DOM perfectly still while the browser waits to surface the
 * SMS one-time-code suggestion, so a live "Resend code in 0:59" countdown and
 * Messages autofill coexist — the shape every major OTP screen uses.
 *
 * (Contrast the previous approach, which ticked the cooldown in the parent and
 * re-rendered the whole OTP screen every second, then "fixed" that by dropping
 * the countdown entirely. Isolation gives us both.)
 *
 * The component owns its own cooldown and in-flight state, so a resend never
 * pushes a render through the parent either. `onResend` re-sends the code; when
 * it resolves the countdown restarts on its own.
 */
export function OtpResend({
  cooldownSeconds = 60,
  onResend,
  dataTrack,
}: {
  /** Seconds to wait before the Resend action re-arms. */
  cooldownSeconds?: number;
  /** Re-send the code. Resolve on success to restart the countdown; reject/throw to leave it armed. */
  onResend: () => Promise<void> | void;
  /** Optional analytics hook forwarded to the Resend button. */
  dataTrack?: string;
}) {
  const [secondsLeft, setSecondsLeft] = useState(cooldownSeconds);
  const [isResending, setIsResending] = useState(false);
  const intervalRef = useRef<number | null>(null);

  const startCountdown = useCallback(() => {
    if (intervalRef.current !== null) window.clearInterval(intervalRef.current);
    setSecondsLeft(cooldownSeconds);
    intervalRef.current = window.setInterval(() => {
      setSecondsLeft((current) => {
        if (current <= 1) {
          if (intervalRef.current !== null) window.clearInterval(intervalRef.current);
          intervalRef.current = null;
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  }, [cooldownSeconds]);

  // Start the countdown on mount (a fresh screen = a freshly-sent code) and stop
  // it on unmount.
  useEffect(() => {
    startCountdown();
    return () => {
      if (intervalRef.current !== null) window.clearInterval(intervalRef.current);
    };
  }, [startCountdown]);

  const handleResend = async () => {
    if (isResending) return;
    setIsResending(true);
    try {
      await onResend();
      startCountdown();
    } catch {
      // Resend failed — leave the button armed for a retry. The caller surfaces
      // the error message; we just don't restart the cooldown.
    } finally {
      setIsResending(false);
    }
  };

  if (secondsLeft > 0) {
    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;
    return (
      <V2Typography variant="meta" muted>
        Resend code in {minutes}:{seconds.toString().padStart(2, "0")}
      </V2Typography>
    );
  }

  return (
    <V2Button
      variant="link"
      onClick={() => void handleResend()}
      disabled={isResending}
      data-track={dataTrack}
    >
      {isResending ? "Sending…" : "Resend code"}
    </V2Button>
  );
}
